// Drafts queue right-rail panel — lists every editable (user_edit) draft
// across the workspace's Pieces, sorted by the user-controlled `sort_order`
// (drag to reorder), with newest-edited as the tiebreaker.
//
// Row anatomy:
//   [drag handle] [select-row button …] [trash button]
// Split into siblings so we never nest interactive elements.
import { useMemo, useState, type HTMLAttributes } from "react";
import { FileText, GripVertical, Search, Trash2 } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import type { ContentBrief, ContentBriefDraft } from "@/lib/composerCore";

type DraftsQueuePanelProps = {
  briefs: ContentBrief[];
  drafts: ContentBriefDraft[];
  activeDraftId?: string | null;
  onSelect: (briefId: string, draftId: string) => void;
  onDelete?: (draftId: string) => void | Promise<void>;
  onReorder?: (orderedDraftIds: string[]) => void | Promise<void>;
};

const isPlaceholderPieceTitle = (t: string | null | undefined): boolean =>
  (t ?? "").trim().toLowerCase() === "untitled draft";

const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleDateString();
};

type RowProps = {
  draft: ContentBriefDraft;
  title: string;
  subtitle: string | null;
  isActive: boolean;
  onSelect: () => void;
  onDelete?: () => void;
  dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
};

const DraftRow = ({
  draft,
  title,
  subtitle,
  isActive,
  onSelect,
  onDelete,
  dragHandleProps,
}: RowProps) => {
  const hoverMuted = isActive ? "" : "group-hover:text-accent-foreground/80";
  return (
    <div
      className={cn(
        "group flex items-stretch gap-1 rounded-md border transition-colors",
        isActive
          ? "border-primary bg-primary/10"
          : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {dragHandleProps && (
        <button
          type="button"
          aria-label="Drag to reorder"
          className={cn(
            "flex cursor-grab touch-none items-center px-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100",
            hoverMuted,
          )}
          {...dragHandleProps}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 px-1.5 py-2 text-left"
      >
        <div className="flex items-start gap-2">
          <FileText className={cn("mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground", hoverMuted)} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{title}</p>
            {subtitle ? (
              <p className={cn("truncate text-[11px] text-muted-foreground", hoverMuted)}>{subtitle}</p>
            ) : null}
            <p className={cn("text-[10px] text-muted-foreground", hoverMuted)}>
              edited {fmtTime(draft.updated_at ?? draft.created_at)}
            </p>
          </div>
        </div>
      </button>
      {onDelete && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Delete draft"
          className={cn(
            "mr-1 h-7 w-7 shrink-0 self-center text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:!text-destructive",
            hoverMuted,
          )}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );


};

type SortableDraftRowProps = RowProps & { id: string };

const SortableDraftRow = (props: SortableDraftRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <DraftRow
        {...props}
        dragHandleProps={{ ...attributes, ...listeners } as HTMLAttributes<HTMLButtonElement>}
      />
    </div>
  );
};

export const DraftsQueuePanel = ({
  briefs,
  drafts,
  activeDraftId,
  onSelect,
  onDelete,
  onReorder,
}: DraftsQueuePanelProps) => {
  const [q, setQ] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const briefById = useMemo(() => new Map(briefs.map((b) => [b.id, b])), [briefs]);

  // Editable drafts, respecting the query snapshot's server-side sort
  // (sort_order asc, updated_at desc). We do NOT resort here — that would
  // fight the user's drag order.
  const editableDrafts = useMemo(
    () => drafts.filter((d) => d.revision_kind === "user_edit"),
    [drafts],
  );

  const filteredDrafts = useMemo(() => {
    if (!q.trim()) return editableDrafts;
    const needle = q.trim().toLowerCase();
    return editableDrafts.filter((d) => {
      const pieceTitle = briefById.get(d.piece_id)?.title ?? "";
      const pieceTitleForSearch = isPlaceholderPieceTitle(pieceTitle) ? "" : pieceTitle;
      const title = (d.draft_title ?? pieceTitleForSearch).toLowerCase();
      return title.includes(needle);
    });
  }, [editableDrafts, q, briefById]);

  const canReorder = !!onReorder && q.trim().length === 0;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = editableDrafts.map((d) => d.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    void onReorder?.(next);
  };

  const handleDelete = (draftId: string, title: string) => {
    if (!onDelete) return;
    const ok = window.confirm(
      `Delete draft "${title}"? This can't be undone.`,
    );
    if (!ok) return;
    void onDelete(draftId);
  };

  const rowFor = (d: ContentBriefDraft) => {
    const brief = briefById.get(d.piece_id);
    const hasRealPieceTitle = !!brief && !isPlaceholderPieceTitle(brief.title);
    const title =
      d.draft_title?.trim() ||
      (hasRealPieceTitle ? brief!.title : "") ||
      "Untitled draft";
    const subtitle = hasRealPieceTitle && brief!.title !== title ? brief!.title : null;
    return {
      id: d.id,
      draft: d,
      title,
      subtitle,
      isActive: d.id === activeDraftId,
      onSelect: () => onSelect(d.piece_id, d.id),
      onDelete: onDelete ? () => handleDelete(d.id, title) : undefined,
    };
  };


  return (
    <div className="flex h-full flex-col gap-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter drafts…"
          className="h-8 pl-7 text-xs"
          aria-label="Filter drafts"
        />
      </div>
      <ScrollArea className="flex-1">
        {filteredDrafts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-muted/20 p-4 text-center">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              No editable drafts yet. Use <strong>Save as Editable Draft</strong> in the Draft Canvas
              header to turn an AI draft into one you can edit.
            </p>
          </div>
        ) : canReorder ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={filteredDrafts.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-1.5 pr-3">
                {filteredDrafts.map((d) => (
                  <li key={d.id}>
                    <SortableDraftRow {...rowFor(d)} />
                  </li>
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        ) : (
          <ul className="space-y-1.5 pr-3">
            {filteredDrafts.map((d) => (
              <li key={d.id}>
                <DraftRow {...rowFor(d)} />
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
      {onReorder && q.trim().length > 0 && (
        <p className="px-1 text-[10px] text-muted-foreground">
          Clear the filter to drag-reorder drafts.
        </p>
      )}
    </div>
  );
};

export default DraftsQueuePanel;
